import { memoAll } from '../../../CodesForFoundation';
import { clamp } from '../../../CodesThatsGottaGoSomewhereElse';
import type { JiraIssueData } from '../../../api/endpoints/jira';
import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { Attribute, ObjectManager, component, property } from '../../../concepts/objects';
import { memoUsing } from '../../../concepts/objects/MemoUsing';
import { round } from '../../../platform/geom/Math';
import { Point } from '../../../platform/geom/Point';
import { Rect } from '../../../platform/geom/Rect';
import { AdapterInfo, CreateAdapterInfo } from '../../canvas/AdapterInfo';
import { EditorSession } from '../../canvas/EditorSession';
import { AutoBringToFront } from '../../canvas/entities/AutoBringToFront';
import { Figure, Physical, PotentialInteraction, RelativeTo, TransformHints, TransformScope } from '../../canvas/entities/Physical';
import { RectangularPhysical } from '../../canvas/entities/RectangularPhysical';
import { UIField } from '../../canvas/entities/UIField';
import { FieldLayout } from '../../canvas/fields/AdaptableField';
import { SimpleTextField } from '../../canvas/fields/SimpleTextField';
import { GraffitiTarget } from '../../canvas/visualization/Graffiti';
import { AutoLayoutSubject } from '../autoLayout/AutoLayout';
import { ConnectTarget } from '../connector/ConnectTarget';
import { Pingable } from '../presentation/Pingable';
import { AtlassianContext } from './AtlassianContext';
import { JiraCardLayout } from './JiraCardLayout';
import { jiraCardPropertyLayout } from './JiraCardPropertyLayout';
import { JiraAssigneeField } from './fields/JiraAssigneeField';
import { JiraCustomField } from './fields/JiraCustomField';
import { JiraIssueTypeField } from './fields/JiraIssueTypeField';
import { JiraParentField } from './fields/JiraParentField';
import { JiraPriorityField } from './fields/JiraPriorityField';
import { JiraStatusField } from './fields/JiraStatusField';
import { JiraSummaryField } from './fields/JiraSummaryField';

const invocation = defineCommand(
    'jiraCardInvoke',
    null,
    (cc, [id]: [string]) => {
        // const dispatch = r.call(CommandDispatcher);
        // dispatch(flip, [[id]]);
    },
    [],
);

const mutation = defineCommand(
    'jiraCardMutate',
    null,
    (cc, [id]: [string]) => {
        const objects = cc.resolve(ObjectManager);
        const card = objects.getMaybe(id, JiraCard);
        if (!card) return;
        cc.call(EditorSession).update(AdapterInfo, CreateAdapterInfo(id, 'field'));
    },
    [],
);

const commit = defineCommand(
    'jiraCardCommit',
    null,
    (cc, []: [string]) => {
        cc.resolve(AtlassianContext).flushIssuePatches();
    },
    [],
);

const jiraCardDef = {
    name: 'JiraCard',
    implements: [
        Physical,
        AutoBringToFront,
        AutoLayoutSubject,
        ConnectTarget,
        Pingable,
        GraffitiTarget
    ],
    invocation,
    mutation,
    commit,
    propertyLayout: jiraCardPropertyLayout
};

export class JiraCardSummary extends SimpleTextField {

    public get layout(): FieldLayout {
        return new FieldLayout(this.owner.cast(JiraCard).layout.summary, 2);
    }
}

export const JiraCardDataRecordAttr = new Attribute<JiraIssueData, JiraCard>('jiraDataRecord', null);

export interface JiraCardCustomInfo {
    key: string;
    name: string;
    value: any;
    type: string;
}

export const JiraCardCustomsVisibleAttr = new Attribute<string[], JiraCard>('jiraCustomsVisible');

@component(jiraCardDef)
export class JiraCard extends RectangularPhysical {

    @property({ type: 'number', default: 360 })
    public width!: number;

    @property({ type: 'number', default: 160 })
    public height!: number;

    @property({ type: 'string' })
    public project!: string;

    @property({ type: 'string' })
    public externalId!: string;

    @property({ type: 'string' })
    public key!: string;

    public get minWidth(): number {
        return 360;
    }

    public get minHeight(): number {
        return Math.max(140, this.layout.base.height);
    }

    @memoUsing(JiraCardDataRecordAttr)
    public get record(): JiraIssueData {
        return JiraCardDataRecordAttr.get(this);
    }

    @memoUsing('record')
    public get customs(): JiraCardCustomInfo[] {
        return Object.entries(this?.record?.custom ?? {})
            .map(([key, value]) => ({ key, ...value }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }

    public get boundary(): boolean {
        return true;
    }

    @memoUsing('width', 'height', 'record', JiraCardCustomsVisibleAttr)
    public get layout() {
        return JiraCardLayout(this);
    }

    @memoUsing('externalId', 'layout', 'record', JiraCardCustomsVisibleAttr)
    public get uiFields(): readonly UIField[] {
        if (!!this.externalId) {
            const show = JiraCardCustomsVisibleAttr.get(this);
            return [
                JiraSummaryField(this),
                JiraParentField(this),
                JiraIssueTypeField(this),
                JiraPriorityField(this),
                JiraStatusField(this),
                JiraAssigneeField(this),
                ...this.customs
                    .filter(x => show.includes(x.key))
                    .filter(x => x.type === 'string' || x.type === 'number' || x.type === 'sprint')
                    .map(x => JiraCustomField(this, x)),
            ];
        }
        else {
            return [
                JiraSummaryField(this),
                JiraAssigneeField(this),
            ];
        }
    }

    public area(to: RelativeTo | Physical = RelativeTo.Parent): Rect {
        return Rect.fromPoints(this.transform(to).apply(this.localBounds.inflate([0, 18]).offset([0, -18])));
    }

    public testInteraction({ type, mpt }: PotentialInteraction, _force?: boolean, _selected?: boolean): [boolean, string] {
        const rpt = this.toRelative(mpt);
        if (this.localBounds.contains(rpt)) {
            return [type !== 'invoke', 'default'];
        }
        return [false, 'none'];
    }

    @memoAll()
    public queryTransformHints(singular: boolean) {
        return TransformHints({
            scaleX: !!singular,
            scaleY: false,
            rotate: false,
            translate: true,
            visible: true,
        });
    }

    public applyTransformation(scope: TransformScope, from: Figure, to: Figure): this {
        // Figures are always model level, so we need to apply the inverse transform of the parent to localize it.
        const tlt = this.modelLocalTransform();
        from = tlt.apply(from);
        to = tlt.apply(to);

        const [a, b, c] = scope.type === 'scale' ? this.jiggleForPotentialMirror(from, to) : to;

        // prev
        const px = from[0].x;
        const pw = Point.vector(from[0], from[1]).length();
        const ph = Point.vector(from[1], from[2]).length();

        // width & height vectors
        const wv = Point.vector(a, b);
        const hv = Point.vector(b, c);

        // Calcualte new center point
        const nc = Rect.fromPoints(to).center();

        // Making use of the TransformScope information, if we can tell that the transform is a scale only upon a single Sticky, we should
        // prevent the sticky from moving so the resize action doesn't feel too strange.  To do this, restrict to cases where x movement
        // is within a single resize range and the width actually changes.
        if (scope.type === 'scale' && scope.items.length === 1) {

            const x = px + clamp(a.x - px, 20);
            const w = Math.max(pw + clamp(wv.length() - pw, 20), this.minWidth);

            if (this.width !== w) {
                this.setTransformationResult(
                    round(x, 4),
                    round(nc.y - (ph / 2), 4),
                    0,
                    round(w, 4),
                    round(this.minHeight, 4),
                );
            }
        }
        else {
            this.setTransformationResult(
                round(nc.x - (pw / 2), 4),
                round(nc.y - (ph / 2), 4),
                round(0, 4),
                round(pw, 4),
                round(this.minHeight, 4),
            );
        }

        return this;
    }

    public sizeToContent() {
        if (this.height !== this.minHeight) {
            this.height = this.minHeight;
        }
    }

    public toHumanString(): string {
        return `JiraCard ("${(this.key).slice(0, 20)}")`;
    }
}
